import { useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { GearSix, EnvelopeSimple, Megaphone, CheckCircle, WarningCircle, GoogleLogo, Copy, CaretDown, LinkBreak, ShareNetwork, CalendarCheck, Robot } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { PasswordInput } from "../components/PasswordInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { SocialConfigDialog, SOCIAL_PROVIDERS } from "../components/SocialConfigDialog";

const inputCls = "h-11 bg-transparent border-white/10 text-white placeholder:text-white/25 focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40";
const emptySmtp = { host: "", port: 587, username: "", password: "", use_tls: true, from_email: "", from_name: "", recipients: "", enabled: true, has_password: false };

const KINDS = {
  comunicazioni: {
    title: "SMTP_Comunicazioni",
    icon: EnvelopeSimple,
    color: "text-violet-400",
    border: "hover:border-violet-400/50",
    description: "Comunicazioni di servizio di PAQ.",
  },
  viki: {
    title: "SMTP_Viki",
    icon: Robot,
    color: "text-cyan-400",
    border: "hover:border-cyan-400/50",
    description: "Comunicazioni di V.I.K.I.",
  },
  marketing: {
    title: "SMTP_Marketing",
    icon: Megaphone,
    color: "text-amber-400",
    border: "hover:border-amber-400/50",
    description: "Invio di comunicazioni marketing e newsletter (dalla pagina Marketing). Configurazione separata per proteggere la reputazione dell'SMTP di servizio.",
  },
};

const SmtpConfigDialog = ({ kind, onClose, onSaved }) => {
  const meta = kind ? KINDS[kind] : null;
  const [smtp, setSmtp] = useState(emptySmtp);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [resend, setResend] = useState(null);

  useEffect(() => {
    if (!kind) return;
    setSmtp(emptySmtp);
    api.get("/admin/settings/smtp")
      .then(({ data }) => {
        const cfg = data[kind];
        setSmtp({ ...cfg, password: "", recipients: (cfg.recipients || []).join(", ") });
      })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
    if (kind !== "marketing") {
      setResend(null);
      api.get("/admin/settings/resend-status").then(({ data }) => setResend(data)).catch(() => {});
    }
  }, [kind]);

  const payload = () => ({
    host: smtp.host, port: Number(smtp.port) || 587, username: smtp.username,
    password: smtp.password || null, use_tls: smtp.use_tls, from_email: smtp.from_email,
    from_name: smtp.from_name || "",
    recipients: (smtp.recipients || "").split(/[,;\n]/).map((r) => r.trim()).filter(Boolean),
    enabled: smtp.enabled,
  });

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/settings/smtp/${kind}`, payload());
      setSmtp({ ...data, password: "", recipients: (data.recipients || []).join(", ") });
      toast.success(`${meta.title} salvato`);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/admin/settings/smtp/${kind}/test`);
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <Dialog open={!!kind} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-surface border-white/10 text-white sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`smtp-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-xl flex items-center gap-2">
            <Icon size={20} weight="duotone" className={meta.color} /> {meta.title}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">{meta.description}</DialogDescription>
        </DialogHeader>
        {kind !== "marketing" && resend && (
          <p className={`text-xs font-semibold flex items-center gap-1.5 ${resend.status === "ok" ? "text-emerald-400" : "text-red-400"}`}
            data-testid="resend-status-badge">
            {resend.status === "ok" ? <CheckCircle size={14} weight="fill" /> : <WarningCircle size={14} weight="fill" />}
            {resend.message}
          </p>
        )}
        <form onSubmit={handleSave} className="space-y-4 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Host SMTP</Label>
              <Input required data-testid={`smtp-${kind}-host-input`} value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} placeholder="smtp.esempio.it" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Porta</Label>
              <Input data-testid={`smtp-${kind}-port-input`} type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: e.target.value })} placeholder="587" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Username</Label>
              <Input data-testid={`smtp-${kind}-username-input`} value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} placeholder="utente@esempio.it" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Password {smtp.has_password && <span className="text-white/30 normal-case tracking-normal">(salvata)</span>}</Label>
              <PasswordInput data-testid={`smtp-${kind}-password-input`} value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} placeholder={smtp.has_password ? "••••••••" : ""} className={inputCls} />
            </div>
            {kind !== "marketing" ? (
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Nome</Label>
                <Input data-testid={`smtp-${kind}-from-name-input`} value={smtp.from_name || ""} onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })} placeholder={kind === "viki" ? "V.I.K.I." : "AreaPAQ"} className={inputCls} />
                <p className="text-[11px] text-white/35">
                  {kind === "viki"
                    ? "È il nome che compare come mittente nelle email inviate da VIKI (promemoria, notifiche, documenti)."
                    : "È il nome che compare come mittente quando ricevi un'email. Le comunicazioni vengono inviate a tutti gli utenti Master con l'email di comunicazione abilitata (pannello Utenti)."}
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Mittente (From)</Label>
                  <Input data-testid={`smtp-${kind}-from-input`} value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} placeholder="portale@areapaq.it" className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Destinatari</Label>
                  <Input data-testid={`smtp-${kind}-recipients-input`} value={smtp.recipients} onChange={(e) => setSmtp({ ...smtp, recipients: e.target.value })} placeholder="a@x.it, b@x.it" className={inputCls} />
                </div>
              </>
            )}
          </div>
          <div className="space-y-3 pt-1">
            <label className="flex items-center gap-2.5 cursor-pointer text-sm" data-testid={`smtp-${kind}-tls-checkbox`}>
              <Checkbox checked={smtp.use_tls} onCheckedChange={(v) => setSmtp({ ...smtp, use_tls: !!v })} className="border-white/30 data-[state=checked]:bg-white data-[state=checked]:text-black" />
              Usa TLS (STARTTLS; per porta 465 viene usato SSL)
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer text-sm" data-testid={`smtp-${kind}-enabled-checkbox`}>
              <Checkbox checked={smtp.enabled} onCheckedChange={(v) => setSmtp({ ...smtp, enabled: !!v })} className="border-white/30 data-[state=checked]:bg-white data-[state=checked]:text-black" />
              {kind === "comunicazioni" ? "Invii automatici attivi (backup settimanale, avvisi)" : kind === "viki" ? "Invio email di VIKI attivo (promemoria e notifiche)" : "Configurazione attiva"}
            </label>
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            <Button type="submit" disabled={saving} data-testid={`smtp-${kind}-save-button`} className="h-11 px-6 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
              {saving ? "Salvataggio..." : "Salva impostazioni"}
            </Button>
            <Button type="button" onClick={handleTest} disabled={testing} data-testid={`smtp-${kind}-test-button`} variant="ghost" className="h-11 px-6 border border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full font-semibold text-sm">
              {testing ? "Invio..." : "Invia email di prova"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ADS_GUIDE = [
  { title: "1. Progetto Google Cloud + API", steps: [
    "Vai su console.cloud.google.com e crea (o seleziona) un progetto",
    'In "API e servizi → Libreria" cerca "Google Ads API" e clicca Abilita',
    'In "API e servizi → Schermata consenso OAuth": tipo Esterno, compila i dati dell\'app, aggiungi la tua email come utente di test e lo scope https://www.googleapis.com/auth/adwords',
  ]},
  { title: "2. Client ID e Client Secret (OAuth)", steps: [
    'In "API e servizi → Credenziali" clicca "Crea credenziali → ID client OAuth"',
    'Tipo di applicazione: "Applicazione web"',
    'In "URI di reindirizzamento autorizzati" incolla l\'URI di reindirizzamento mostrato qui sopra (aggiungi sia quello di anteprima sia quello di produzione www.areapaq.it)',
    "Copia Client ID e Client Secret nei campi di questa scheda e salva",
  ]},
  { title: "3. Developer Token (Google Ads)", steps: [
    "Accedi su ads.google.com con un account AMMINISTRATORE (meglio se account manager/MCC)",
    'Vai su "Strumenti e impostazioni → Configurazione → Centro API"',
    'Copia il Developer token. Con livello "Accesso di prova" funziona solo su account di test; per gli account reali richiedi da lì l\'"Accesso di base" (approvazione Google in 1-2 giorni)',
  ]},
  { title: "4. Collega e usa", steps: [
    'Salva le credenziali, poi clicca "Accedi con Google" e autorizza con l\'account Google che ha accesso a Google Ads',
    "Vai su Marketing → Google Ads e seleziona l'account da gestire (se ne hai più di uno)",
  ]},
];

const GoogleAdsConfigDialog = ({ open, onClose, cfg, onSaved }) => {
  const [form, setForm] = useState({ client_id: "", client_secret: "", developer_token: "", login_customer_id: "" });
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    if (open) setForm({ client_id: cfg?.client_id || "", client_secret: "", developer_token: "", login_customer_id: cfg?.login_customer_id || "" });
  }, [open, cfg]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/ads/oauth/credentials", form);
      toast.success(data.message);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/admin/ads/oauth/start");
      window.location.assign(data.authorization_url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const { data } = await api.post("/admin/ads/oauth/disconnect");
      toast.success(data.message);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDisconnecting(false);
    }
  };

  const copyRedirect = () => {
    navigator.clipboard.writeText(cfg?.redirect_uri || "").then(() => toast.success("URI copiato"));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-white/10 text-white sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="ads-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-xl flex items-center gap-2">
            <GoogleLogo size={20} weight="duotone" className="text-blue-400" /> Google_Ads
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Collega AREAPAQ all'API Google Ads: salva le credenziali, poi autorizza con "Accedi con Google". Tutto viene cifrato nel database.
          </DialogDescription>
        </DialogHeader>

        {cfg?.connected ? (
          <p className="text-xs font-semibold text-emerald-400 flex items-center gap-1.5" data-testid="ads-connection-status">
            <CheckCircle size={14} weight="fill" /> Account Google collegato{cfg.customer_id ? <> — account attivo {cfg.customer_id}</> : " — seleziona l'account in Marketing → Google Ads"}
          </p>
        ) : (
          <p className="text-xs font-semibold text-amber-400 flex items-center gap-1.5" data-testid="ads-connection-status">
            <WarningCircle size={14} weight="fill" /> {cfg?.has_credentials ? "Credenziali salvate — manca l'autorizzazione Google" : "Non configurato"}
          </p>
        )}

        <form onSubmit={handleSave} className="space-y-4 mt-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">OAuth Client ID</Label>
              <Input required data-testid="ads-client-id-input" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} placeholder="xxxx.apps.googleusercontent.com" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">OAuth Client Secret {cfg?.has_credentials && <span className="text-white/30 normal-case tracking-normal">(salvato)</span>}</Label>
              <PasswordInput data-testid="ads-client-secret-input" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} placeholder={cfg?.has_credentials ? "••••••••" : ""} className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Developer Token {cfg?.has_credentials && <span className="text-white/30 normal-case tracking-normal">(salvato)</span>}</Label>
              <PasswordInput data-testid="ads-developer-token-input" value={form.developer_token} onChange={(e) => setForm({ ...form, developer_token: e.target.value })} placeholder={cfg?.has_credentials ? "••••••••" : ""} className={inputCls} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Login Customer ID (MCC, facoltativo)</Label>
              <Input data-testid="ads-login-customer-input" value={form.login_customer_id} onChange={(e) => setForm({ ...form, login_customer_id: e.target.value })} placeholder="solo se usi un account manager (10 cifre)" className={inputCls} />
            </div>
          </div>
          <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10">
            <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">URI di reindirizzamento (da registrare su Google Cloud)</p>
            <div className="flex items-center gap-2">
              <code className="text-[11px] text-sky-300 break-all flex-1" data-testid="ads-redirect-uri">{cfg?.redirect_uri || "..."}</code>
              <Button type="button" onClick={copyRedirect} variant="ghost" data-testid="ads-copy-redirect-button"
                className="h-8 w-8 p-0 shrink-0 border border-white/15 text-white/60 hover:text-white hover:bg-white/5 rounded-full">
                <Copy size={13} />
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 pt-1">
            <Button type="submit" disabled={saving} data-testid="ads-settings-save-button" className="h-11 px-6 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
              {saving ? "Salvataggio..." : "Salva credenziali"}
            </Button>
            <Button type="button" onClick={handleConnect} disabled={connecting || !cfg?.has_credentials} data-testid="ads-google-signin-button"
              className="h-11 px-6 bg-blue-500 text-white hover:bg-blue-400 rounded-full font-semibold text-sm disabled:opacity-40">
              <GoogleLogo size={16} weight="bold" className="mr-1.5" /> {connecting ? "Reindirizzamento..." : "Accedi con Google"}
            </Button>
            {cfg?.connected && (
              <Button type="button" onClick={handleDisconnect} disabled={disconnecting} data-testid="ads-disconnect-button" variant="ghost"
                className="h-11 px-5 border border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-full font-semibold text-sm">
                <LinkBreak size={15} className="mr-1.5" /> {disconnecting ? "..." : "Disconnetti"}
              </Button>
            )}
          </div>
        </form>

        <div className="mt-2 rounded-lg border border-white/10 overflow-hidden">
          <button type="button" data-testid="ads-guide-toggle" onClick={() => setGuideOpen((v) => !v)}
            className="w-full flex items-center gap-2 p-3 text-left hover:bg-white/[0.03] transition-colors">
            <span className="text-xs font-semibold text-white/70 flex-1">Guida: come ottenere le credenziali</span>
            <CaretDown size={14} weight="bold" className={`text-white/40 transition-transform duration-200 ${guideOpen ? "rotate-180" : ""}`} />
          </button>
          {guideOpen && (
            <div className="px-4 pb-4 pt-1 border-t border-white/5 space-y-4" data-testid="ads-guide-content">
              {ADS_GUIDE.map((sec) => (
                <div key={sec.title}>
                  <p className="text-xs font-semibold text-white/80 mb-1.5">{sec.title}</p>
                  <ul className="space-y-1">
                    {sec.steps.map((s, i) => (
                      <li key={i} className="text-[11px] text-slate-400 leading-relaxed pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[7px] before:w-1 before:h-1 before:rounded-full before:bg-white/30">{s}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function SettingsPage() {
  const [configs, setConfigs] = useState(null);
  const [openKind, setOpenKind] = useState(null);
  const [adsCfg, setAdsCfg] = useState(null);
  const [adsOpen, setAdsOpen] = useState(false);
  const [socialCfg, setSocialCfg] = useState(null);
  const [socialOpen, setSocialOpen] = useState(null);
  const [socialChooser, setSocialChooser] = useState(false);
  const [commsSched, setCommsSched] = useState(null);

  const loadConfigs = () => api.get("/admin/settings/smtp").then(({ data }) => setConfigs(data)).catch(() => {});
  const loadAds = () => api.get("/admin/ads/oauth/config").then(({ data }) => setAdsCfg(data)).catch(() => {});
  const loadSocial = () => api.get("/admin/social/config").then(({ data }) => setSocialCfg(data)).catch(() => {});
  useEffect(() => {
    loadConfigs();
    loadAds();
    loadSocial();
    api.get("/admin/maintenance/comms-schedule").then(({ data }) => setCommsSched(data)).catch(() => {});
  }, []);

  return (
    <main className="px-6 md:px-10 py-10 max-w-5xl" data-testid="settings-page">
      <div className="mb-10 animate-fade-up">
        <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2 flex items-center gap-1.5">
          <GearSix size={13} className="text-violet-400" /> Configurazione portale
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter">Impostazioni</h1>
        <p className="text-sm text-slate-400 mt-3 max-w-2xl">
          Gestione centralizzata delle configurazioni SMTP del portale. Clicca su una scheda per configurare.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {Object.entries(KINDS).map(([kind, meta], i) => {
          const Icon = meta.icon;
          const configured = !!configs?.[kind]?.host;
          return (
            <Fragment key={kind}>
            <button
              type="button"
              data-testid={`smtp-${kind}-tile`}
              onClick={() => setOpenKind(kind)}
              className={`group text-left p-8 rounded-lg bg-surface border border-surface-border ${meta.border} transition-colors duration-200 animate-fade-up`}
              style={{ animationDelay: `${80 + i * 80}ms` }}
            >
              <Icon size={44} weight="duotone" className={`${meta.color} mb-5 transition-transform duration-200 group-hover:scale-110`} />
              <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">{meta.title}</h2>
              <p className="text-sm text-slate-400 mb-5">{meta.description}</p>
              {configs && (
                configured ? (
                  <p className="text-xs font-semibold text-emerald-400 flex items-center gap-1.5" data-testid={`smtp-${kind}-status`}>
                    <CheckCircle size={14} weight="fill" /> Configurato — {configs[kind].host}
                  </p>
                ) : (
                  <p className="text-xs font-semibold text-amber-400 flex items-center gap-1.5" data-testid={`smtp-${kind}-status`}>
                    <WarningCircle size={14} weight="fill" /> Non configurato — clicca per configurare
                  </p>
                )
              )}
            </button>
            </Fragment>
          );
        })}
        <button
          type="button"
          data-testid="social-settings-tile"
          onClick={() => setSocialChooser(true)}
          className="group text-left p-8 rounded-lg bg-surface border border-surface-border hover:border-fuchsia-400/50 transition-colors duration-200 animate-fade-up"
          style={{ animationDelay: "240ms" }}
        >
          <ShareNetwork size={44} weight="duotone" className="text-fuchsia-400 mb-5 transition-transform duration-200 group-hover:scale-110" />
          <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Social</h2>
          <p className="text-sm text-slate-400 mb-5">Connessioni ai canali social: Meta (Facebook/Instagram) e TikTok Ads, con credenziali e guide.</p>
          {socialCfg && (
            (socialCfg.meta?.connected || socialCfg.tiktok?.connected) ? (
              <p className="text-xs font-semibold text-emerald-400 flex items-center gap-1.5" data-testid="social-settings-status">
                <CheckCircle size={14} weight="fill" /> {[socialCfg.meta?.connected && "Meta", socialCfg.tiktok?.connected && "TikTok"].filter(Boolean).join(" + ")} collegato
              </p>
            ) : (
              <p className="text-xs font-semibold text-amber-400 flex items-center gap-1.5" data-testid="social-settings-status">
                <WarningCircle size={14} weight="fill" /> {(socialCfg.meta?.has_credentials || socialCfg.tiktok?.has_credentials) ? "Credenziali salvate — collega gli account" : "Non configurato — clicca per configurare"}
              </p>
            )
          )}
        </button>
        <button
          type="button"
          data-testid="ads-settings-tile"
          onClick={() => setAdsOpen(true)}
          className="group text-left p-8 rounded-lg bg-surface border border-surface-border hover:border-blue-400/50 transition-colors duration-200 animate-fade-up"
          style={{ animationDelay: "320ms" }}
        >
          <GoogleLogo size={44} weight="duotone" className="text-blue-400 mb-5 transition-transform duration-200 group-hover:scale-110" />
          <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Google_Ads</h2>
          <p className="text-sm text-slate-400 mb-5">Connessione OAuth all'API Google Ads: credenziali, "Accedi con Google" e guida per ottenere Client ID, Secret e Developer Token.</p>
          {adsCfg && (
            adsCfg.connected ? (
              <p className="text-xs font-semibold text-emerald-400 flex items-center gap-1.5" data-testid="ads-settings-status">
                <CheckCircle size={14} weight="fill" /> Collegato{adsCfg.customer_id ? ` — account ${adsCfg.customer_id}` : ""}
              </p>
            ) : (
              <p className="text-xs font-semibold text-amber-400 flex items-center gap-1.5" data-testid="ads-settings-status">
                <WarningCircle size={14} weight="fill" /> {adsCfg.has_credentials ? "Credenziali salvate — collega l'account" : "Non configurato — clicca per configurare"}
              </p>
            )
          )}
        </button>
        <Link
          to="/impostazioni/servizi"
          data-testid="servizi-settings-tile"
          className="group text-left p-8 rounded-lg bg-surface border border-surface-border hover:border-emerald-400/50 transition-colors duration-200 animate-fade-up block"
          style={{ animationDelay: "400ms" }}
        >
          <CalendarCheck size={44} weight="duotone" className="text-emerald-400 mb-5 transition-transform duration-200 group-hover:scale-110" />
          <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Servizi</h2>
          <p className="text-sm text-slate-400 mb-5">Centro di controllo delle comunicazioni automatizzate: quali inviare, in che giorno e a che ora (backup, report settimanale, indicizzazione) e notifiche push.</p>
          {commsSched && (
            <p className="text-xs font-semibold text-emerald-400 flex items-center gap-1.5" data-testid="servizi-settings-status">
              <CheckCircle size={14} weight="fill" /> {["backup", "report", "autoindex"].filter((k) => commsSched[k]?.enabled).length}/3 comunicazioni attive — clicca per gestire
            </p>
          )}
        </Link>
      </div>
      <Dialog open={socialChooser} onOpenChange={setSocialChooser}>
        <DialogContent className="bg-surface border-white/10 text-white sm:max-w-md" data-testid="social-chooser-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-xl flex items-center gap-2">
              <ShareNetwork size={20} weight="duotone" className="text-fuchsia-400" /> Social
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Scegli il canale da configurare.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-1">
            {Object.entries(SOCIAL_PROVIDERS).map(([prov, meta]) => {
              const Icon = meta.icon;
              const c = socialCfg?.[prov];
              return (
                <button key={prov} type="button" data-testid={`social-${prov}-tile`}
                  onClick={() => { setSocialChooser(false); setSocialOpen(prov); }}
                  className={`group text-left p-5 rounded-lg bg-white/[0.02] border border-white/10 ${meta.border} transition-colors`}>
                  <Icon size={32} weight="duotone" className={`${meta.color} mb-3 transition-transform duration-200 group-hover:scale-110`} />
                  <p className="font-heading text-lg font-semibold tracking-tight">{prov === "meta" ? "Meta_Social" : "TikTok_Ads"}</p>
                  {c && (
                    c.connected ? (
                      <p className="text-[10px] font-semibold text-emerald-400 mt-1.5 flex items-center gap-1" data-testid={`social-${prov}-tile-status`}>
                        <CheckCircle size={11} weight="fill" /> Collegato
                      </p>
                    ) : (
                      <p className="text-[10px] font-semibold text-amber-400 mt-1.5 flex items-center gap-1" data-testid={`social-${prov}-tile-status`}>
                        <WarningCircle size={11} weight="fill" /> {c.has_credentials ? "Da collegare" : "Non configurato"}
                      </p>
                    )
                  )}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
      <SmtpConfigDialog kind={openKind} onClose={() => setOpenKind(null)} onSaved={loadConfigs} />
      <GoogleAdsConfigDialog open={adsOpen} onClose={() => setAdsOpen(false)} cfg={adsCfg} onSaved={loadAds} />
      <SocialConfigDialog provider={socialOpen} onClose={() => setSocialOpen(null)} cfg={socialOpen ? socialCfg?.[socialOpen] : null} onSaved={loadSocial} />
    </main>
  );
}
